/**
 * Корневой компонент приложения АСУБП.
 * Оборачивает приложение в провайдеры: MUI Theme, React Query, Router, Snackbar.
 */
import { useEffect, useMemo, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

import { useUIStore } from '@/stores/uiStore';
import { createAppTheme } from '@/theme/config';
import ErrorBoundary from '@/components/ErrorBoundary';
import router from '@/router';

// Устанавливаем русскую локаль для dayjs
dayjs.locale('ru');

/** Клиент React Query с настройками по умолчанию */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 минут
    },
  },
});

/** Вычисляет реальный режим темы ('dark' | 'light') из настройки включая 'system' */
function resolveThemeMode(theme: 'dark' | 'light' | 'system'): 'dark' | 'light' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

const App: React.FC = () => {
  const currentTheme = useUIStore((state) => state.theme);
  const [resolvedMode, setResolvedMode] = useState<'dark' | 'light'>(resolveThemeMode(currentTheme));

  useEffect(() => {
    setResolvedMode(resolveThemeMode(currentTheme));
    if (currentTheme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => setResolvedMode(e.matches ? 'dark' : 'light');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [currentTheme]);

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <SnackbarProvider
              maxSnack={3}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              autoHideDuration={4000}
            >
              <RouterProvider router={router} />
            </SnackbarProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
